import React, { useState, useEffect } from 'react';
import ArtistLayout from '../../components/artist/ArtistLayout';
import { CreditCard, Package, CheckCircle, Clock } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ArtistDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('artist_token');
      const response = await apiRequest(`${BACKEND_URL}/api/artist/dashboard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setStats(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setLoading(false);
    }
  };

  const statCards = stats ? [
    {
      title: 'Digital Cards',
      value: stats.total_digital_cards,
      pending: stats.pending_digital_cards,
      published: stats.published_digital_cards,
      icon: CreditCard,
      color: 'purple'
    },
    {
      title: 'Total Orders',
      value: stats.total_orders,
      icon: Package,
      color: 'green'
    }
  ] : [];

  if (loading) {
    return (
      <ArtistLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </ArtistLayout>
    );
  }

  return (
    <ArtistLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-navy mb-2">Dashboard</h1>
          <p className="text-gray-600">Welcome back! Here's your overview</p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            const colorClasses = {
              blue: 'bg-blue-50 text-blue-600',
              purple: 'bg-purple-50 text-purple-600',
              green: 'bg-green-50 text-green-600'
            };

            return (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl ${colorClasses[card.color]} flex items-center justify-center`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
                
                <h3 className="text-3xl font-bold text-navy mb-2">{card.value}</h3>
                <p className="text-gray-600 font-medium mb-3">{card.title}</p>
                
                {card.pending !== undefined && (
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <span className="text-gray-600">
                        {card.pending} Pending
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-gray-600">
                        {card.published} Published
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Quick Actions</h2>
          <p className="text-purple-100 mb-6">
            Submit new cards for approval and manage your existing cards
          </p>
          <div className="flex gap-4">
            <a
              href="/artist/cards"
              className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
            >
              Manage Cards
            </a>
            <a
              href="/artist/orders"
              className="bg-purple-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-400 transition-colors"
            >
              View Orders
            </a>
          </div>
        </div>
      </div>
    </ArtistLayout>
  );
}
